"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CircleCheck, Clock, FileText, Loader } from "lucide-react";
export default function HowTheAuditWorks() {
  const steps = [{
    icon: FileText,
    title: "Submit Your Request",
    description: "Fill out the form below with your studio or nonprofit details. We'll analyze your current administrative workflow."
  }, {
    icon: Clock,
    title: "We Audit Your Systems",
    description: "Our team examines your processes to identify friction points, inefficiencies, and automation opportunities."
  }, {
    icon: CircleCheck,
    title: "Receive Your Blueprint",
    description: "Get a custom roadmap showing exactly how AI can reclaim your time and eliminate administrative chaos."
  }];
  return <section className="bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-display font-bold text-foreground mb-4">
            How the Audit Works
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three simple steps to discover your automation potential and start reclaiming the rhythm of your work.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3 mb-16">
          {steps.map((step, i) => <StepCard key={i} step={step} index={i} />)}
        </div>

        <div className="max-w-2xl mx-auto">
          <FormProvider className="w-full" formId="audit-request-form-section" sectionName="how-the-audit-works" defaultValues={{
          name: "",
          email: "",
          organization: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          organization: {
            required: true,
            minLength: 2,
            maxLength: 200
          },
          message: {
            required: true,
            minLength: 10,
            maxLength: 1000
          }
        }} submitText="Request Your Audit" successMessage="Thank you! We'll analyze your needs and contact you within 24 hours.">
            <AuditRequestForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function StepCard({
  step,
  index
}: {
  step: {
    icon: any;
    title: string;
    description: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className="transition-all duration-200 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="h-full p-6 border-2 border-border hover:border-primary hover:shadow-lg transition-all duration-200">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon className="size-8 text-primary" />
          </div>
          <h3 className="text-xl font-display font-semibold text-foreground">
            {step.title}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {step.description}
          </p>
        </div>
      </Card>
    </div>;
}
function AuditRequestForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="p-8 border-2 border-primary bg-primary-light">
        <div className="text-center">
          <CircleCheck className="size-16 text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-display font-bold text-foreground mb-2">
            Request Submitted!
          </h3>
          <p className="text-muted-foreground mb-6">{successMessage}</p>
          <Button onClick={resetForm} variant="outline">
            Submit Another Request
          </Button>
        </div>
      </Card>;
  }
  return <div ref={ref} className="transition-all duration-250 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateX(0)" : "translateX(40px)"
  }}>
      <Card className="p-8 border-2 border-border">
        <div className="mb-6 text-center">
          <h3 className="text-2xl font-display font-bold text-foreground mb-2">
            Request Your Audit
          </h3>
          <p className="text-muted-foreground text-sm">
            Tell us about your organization and we'll identify your automation opportunities.
          </p>
        </div>

        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <FormField control={control} name="name" rules={{
            required: "Name is required",
            minLength: {
              value: 2,
              message: "Minimum 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className="border-2 focus-visible:border-primary focus-visible:ring-primary/50" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" className="border-2 focus-visible:border-primary focus-visible:ring-primary/50" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="organization" rules={{
            required: "Organization name is required",
            minLength: {
              value: 2,
              message: "Minimum 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Studio/Organization Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your Dance Studio or Nonprofit" className="border-2 focus-visible:border-primary focus-visible:ring-primary/50" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" rules={{
            required: "Message is required",
            minLength: {
              value: 10,
              message: "Minimum 10 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Tell Us About Your Challenges</FormLabel>
                  <FormControl>
                    <textarea placeholder="Describe your current administrative pain points..." rows={4} className="flex w-full rounded-md border-2 border-input bg-transparent px-3 py-2 text-base shadow-xs transition-all outline-none placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border-2 border-destructive rounded-md">
                <p className="text-destructive text-sm font-medium">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold transition-all duration-200 hover:scale-104">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-5" />
                  Submitting...
                </> : 'Request Your Audit'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>;
}